import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { CLAIM_STAGES, deadlineStatusLabel } from '@shared/lib/statusLabels';
import type { Deadline } from '@entities/legal/types';
import { ClaimDocumentsSection } from '../ClaimDocumentsSection';
import { EntityPartiesSection } from '../EntityPartiesSection';
import { DEADLINE_KIND_LABELS, Empty, Row, Section } from './styles';

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  flex-wrap: wrap;
`;

const StageForm = styled.form`
  display: flex;
  gap: 8px;
  align-items: stretch;
`;

const StageRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  flex-wrap: wrap;
`;

const DeleteButtonWrapper = styled.div`
  margin-left: auto;
`;

interface ClaimDisputeSectionProps {
  claimId: string;
  claimStage: string;
  claimAnalysisStatus: string;
  hasOriginalFileHash: boolean;
  hasContractLinked: boolean;
  isIncoming: boolean;
  deadlines: Deadline[];
  isProcessing: boolean;
  isReanalyzing: boolean;
  can_write: boolean;
  onOpenCounterparty: (id: string) => void;
  onReanalyze: () => void;
  onOpenAudit: () => void;
  onOpenRedirect: () => void;
  onUpdateStage: (stage: string) => Promise<void>;
  isUpdatingStage: boolean;
  onOpenConfirmDelete: () => void;
  isDeleting: boolean;
}

export function ClaimDisputeSection({
  claimId,
  claimStage,
  claimAnalysisStatus,
  hasOriginalFileHash,
  hasContractLinked,
  isIncoming,
  deadlines,
  isProcessing,
  isReanalyzing,
  can_write,
  onOpenCounterparty,
  onReanalyze,
  onOpenAudit,
  onOpenRedirect,
  onUpdateStage,
  isUpdatingStage,
  onOpenConfirmDelete,
  isDeleting,
}: ClaimDisputeSectionProps) {
  const [stage, setStage] = useState(claimStage);

  async function handleStageSubmit(e: FormEvent) {
    e.preventDefault();
    if (!stage) return;
    await onUpdateStage(stage);
  }

  return (
    <>
      <ClaimDocumentsSection claimId={claimId} uploadDisabled={isProcessing} />

      <EntityPartiesSection
        scope="claim"
        entityId={claimId}
        isAnalyzing={isProcessing}
        onOpenCounterparty={onOpenCounterparty}
      />

      <Section>
        <h4>Сроки на контроле ({deadlines.length})</h4>
        {deadlines.length === 0 ? (
          <Empty>Активных сроков нет.</Empty>
        ) : (
          deadlines.map((d) => {
            const days = Math.round((new Date(d.due_at).getTime() - Date.now()) / 86_400_000);
            return (
              <Row key={d.id}>
                <span>{new Date(d.due_at).toLocaleDateString('ru-RU')}</span>
                <span style={{ flex: 1 }}>
                  <strong>{DEADLINE_KIND_LABELS[d.kind] ?? d.kind}</strong>
                  <span style={{ marginLeft: 8, opacity: 0.7 }}>
                    · {days < 0 ? `просрочено на ${-days} дн.` : `осталось ${days} дн.`}
                  </span>
                </span>
                <Badge>{deadlineStatusLabel(d.status)}</Badge>
              </Row>
            );
          })
        )}
      </Section>

      <Section>
        <h4>Действия</h4>
        <ActionsRow>
          {hasOriginalFileHash && (
            <Button
              type="button"
              onClick={onReanalyze}
              disabled={isReanalyzing || isProcessing || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isReanalyzing || isProcessing ? 'Анализ идёт…' : 'Перезапустить анализ'}
            </Button>
          )}
          <Button
            type="button"
            $variant="secondary"
            onClick={onOpenAudit}
            disabled={claimAnalysisStatus !== 'analyzed' || !can_write}
            title={
              !can_write
                ? 'Только чтение'
                : claimAnalysisStatus === 'analyzed'
                  ? 'Скормить наш анализ внешнему ИИ и загрузить аудит обратно'
                  : 'Доступно когда анализ завершён'
            }
          >
            Внешний аудит
          </Button>
          {isIncoming && (
            <Button
              type="button"
              $variant="secondary"
              onClick={onOpenRedirect}
              disabled={claimAnalysisStatus !== 'analyzed' || !hasContractLinked || !can_write}
              title={
                !can_write
                  ? 'Только чтение'
                  : !hasContractLinked
                    ? 'Сначала свяжите договор генподряда — без него перевыставить нельзя'
                    : claimAnalysisStatus !== 'analyzed'
                      ? 'Доступно когда анализ завершён'
                      : 'Сгенерировать претензию субподрядчику в порядке регресса'
              }
            >
              Перевыставить на субподрядчика
            </Button>
          )}
        </ActionsRow>
      </Section>

      <Section>
        <h4>Стадия и завершение</h4>
        <StageRow>
          <StageForm onSubmit={handleStageSubmit}>
            <Select
              id="stage"
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              aria-label="Стадия"
              title="Стадия дела"
            >
              {CLAIM_STAGES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
            <Button
              type="submit"
              disabled={isUpdatingStage || stage === claimStage || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              Сохранить стадию
            </Button>
          </StageForm>
          <DeleteButtonWrapper>
            <Button
              type="button"
              $variant="secondary"
              onClick={onOpenConfirmDelete}
              disabled={isDeleting || !can_write}
              title={!can_write ? 'Только чтение' : 'Удалить эту претензию/иск целиком (необратимо)'}
            >
              Удалить
            </Button>
          </DeleteButtonWrapper>
        </StageRow>
      </Section>
    </>
  );
}
